<template>
    <Row>
        <Col span="16">
            <h4>After Effects export preset</h4>
            <p class="tooltip">Control and adjust the rendering format.</p>
        </Col>
        <Col span="8">
            <Select
                v-model="modelValue.outputModule"
                size="small"
                data-testid="download-modal__download-output-module-select"
                transfer
            >
                <Option
                    v-for="option in OUTPUT_MODULE_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                    :data-testid="`download-modal__download-output-module-select-${option.value}`"
                >
                    {{ option.label }}
                </Option>
            </Select>
        </Col>
    </Row>

    <Row>
        <Col span="16">
            <h4>Post encoding with ffmpeg</h4>
            <p class="tooltip">Type of the post encoding to apply to the output file.</p>
        </Col>
        <Col span="8">
            <Select
                v-model="modelValue.postEncoding"
                size="small"
                data-testid="download-modal__download-video-post-encoding-select"
                transfer
            >
                <Option
                    v-for="option in POST_ENCODING_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                    :data-testid="`download-modal__download-video-post-encoding-select-${option.value}`"
                >
                    {{ option.label }}
                </Option>
            </Select>
        </Col>
    </Row>

    <Row v-if="modelValue.postEncoding === PlainlyPostEncoding.SCALE">
        <Col span="16">
            <h4>Resolution scale percentage</h4>
            <p class="tooltip">
                Example: 50 scales the video resolution down in half, and 200 doubles it up. Minimum is 1, maximum is
                400.
            </p>
            <p class="error">{{ scalingPercentageError }}</p>
        </Col>
        <Col span="8">
            <InputNumber
                v-model="modelValue.scalingPercentage"
                placeholder="100"
                data-testid="download-modal__scaling-percentage-input"
                class="percentage-input"
                type="number"
                :min="1"
                :max="400"
            />
        </Col>
    </Row>

    <Row v-if="modelValue.postEncoding === PlainlyPostEncoding.CUSTOM">
        <Col span="16">
            <h4>Encoding format</h4>
            <p class="tooltip">Format of the post-encoded file.</p>
        </Col>
        <Col span="8">
            <Select
                v-model="modelValue.encodingFormat"
                size="small"
                data-testid="download-modal__download-video-encoding-format-select"
                transfer
            >
                <Option
                    v-for="option in ENCODING_FORMAT_OPTIONS"
                    :key="option.value"
                    :value="option.value"
                    :data-testid="`download-modal__download-video-encoding-format-select-${option.value}`"
                >
                    {{ option.label }}
                </Option>
            </Select>
        </Col>
    </Row>

    <Row v-if="modelValue.postEncoding === PlainlyPostEncoding.CUSTOM">
        <Col span="16">
            <h4>Custom ffmpeg params</h4>
            <p class="tooltip">Encoding params passed to ffmpeg for post encoding.</p>
        </Col>
        <Col span="8">
            <Input
                v-model="modelValue.encodingParamsLine"
                placeholder="Encoding params line"
                data-testid="download-modal__encoding-params-line-input"
            />
        </Col>
    </Row>

    <Divider />

    <div>
        <h4>Output file details, based on your selections:</h4>
        <div v-if="modelValue.postEncoding === PlainlyPostEncoding.DEFAULT">
            <ul>
                <li>- libx264 codec</li>
                <li>- yuv420p pixel format</li>
                <li>- aac audio codec</li>
                <li>- 25 fps</li>
            </ul>
        </div>

        <p v-else-if="modelValue.postEncoding === PlainlyPostEncoding.NONE">
            No post encoding step, output is defined by After Effects export.
        </p>
        <p v-else-if="modelValue.postEncoding === PlainlyPostEncoding.SMALLEST">
            Smallest possible file size with an acceptable quality using the H.265 codec.
        </p>
        <p v-else-if="modelValue.postEncoding === PlainlyPostEncoding.SCALE">
            Render scaled to {{ modelValue.scalingPercentage }}%.
        </p>
        <div v-else-if="modelValue.postEncoding === PlainlyPostEncoding.CUSTOM">
            <p>Output format: {{ modelValue.encodingFormat?.replace("_", " ") }}</p>
            <p v-if="modelValue.encodingParamsLine">Encoding params line {{ modelValue.encodingParamsLine }}.</p>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onUnmounted } from "vue";
import { useDownloadAEVideo } from "@/composables/download/useDownloadAEVideo";
import { PlainlyPostEncoding } from "@/utils/CreativeIntelligenceApi/plainly-options";

import { OUTPUT_MODULE_OPTIONS, POST_ENCODING_OPTIONS, ENCODING_FORMAT_OPTIONS } from "@/components/Download/consts";

const { modelValue, resetValues, scalingPercentageError } = useDownloadAEVideo();

onUnmounted(() => {
    resetValues();
});
</script>

<style lang="scss" scoped>
@use "@style/variables" as *;
.percentage-input {
    float: right;
}

.tooltip {
    font-size: 12.5px;
}

.error {
    color: $error-color;
    font-size: 10px;
    height: 10px;
}
</style>
